import React, { useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DriversComponent from './DriversComponent';
import { useTranslation } from 'react-i18next';
import { append, remove } from 'ramda';
import { useChangeTrackingLens, get, over } from '@totalsoft/change-tracking-react';
import { DRIVERS_QUERY } from '../queries';
import { SAVE_DRIVERS } from '../mutations';
import { DRIVERS_DATA_SAVED } from '../subscriptions';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useToast, useSimpleError } from 'hooks';
import { useQueryWithSimpleErrorHandling, useParams } from 'hooks';
import { isDirty } from '@totalsoft/change-tracking';
import { shape, required, items, fromModel, unique, when, stopOnFirstFailure, concatFailure, maxLength } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isCnp, isSeriesAndNumber, isPhone } from 'utils/simple-validations/validations';
import { Prompt } from 'react-router-dom';

const validator =
  [
    unique(x => x.uniqueID || {}, 'Offer.Edit.DriverData.UniqueID'),
    items(
      fromModel(item =>
        shape({
          uniqueID: [required, isCnp] |> stopOnFirstFailure |> when(item.agreesWithPersonalDataUse === true),
          identityCardNo: [required, isSeriesAndNumber] |> stopOnFirstFailure |> when(item.agreesWithPersonalDataUse === true),
          firstName: [required, maxLength(150)] |> stopOnFirstFailure |> when(item.agreesWithPersonalDataUse === true),
          lastName: [required, maxLength(150)] |> stopOnFirstFailure |> when(item.agreesWithPersonalDataUse === true),
          phoneNumber: [required, isPhone(item.phoneCountryCode)] |> stopOnFirstFailure |> when(item.agreesWithPersonalDataUse === true)
        })
      )
    )
  ] |> concatFailure;

const DriversContainer = props => {
  const { documentId, readOnly, dirtyInfoRef, setDirtyInfoRef } = props;
  const showError = useSimpleError();
  const addToast = useToast();
  const { t } = useTranslation();
  const defaultCountryCode = useParams('DefaultCountryCode');
  const [validation, validate, resetValidation] = useDirtyFieldValidation(validator);

  // #region State

  const [saveDisabled, setSaveDisabled] = useState(true);
  const [addDisabled, setAddDisabled] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);

  // #endregion

  const defaultExpanded = useMemo(() => {
    return (lens |> get).length !== 0 ? true : false;
  }, [lens]);

  const defaultDriver = useMemo(
    () => ({
      id: null,
      uniqueID: '',
      agreesWithPersonalDataUse: false,
      identityCardNo: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneCountryCode: defaultCountryCode,
      isExpanded: true
    }),
    [defaultCountryCode]
  );

  const setDriversData = useCallback(
    e => {
      resetLens(
        e?.driverData.drivers.map(x => {
          return {
            ...x,
            agreesWithPersonalDataUse: true,
            isExpanded: false
          };
        })
      );
      setSaveDisabled(true);
      setAddDisabled(false);
    },
    [resetLens]
  );

  // #region Queries

  const { loading, error } = useQueryWithSimpleErrorHandling(DRIVERS_QUERY, {
    variables: { documentId },
    onCompleted: e => setDriversData(e),
    notifyOnNetworkStatusChange: true
  });

  // #endregion

  // #region Mutations

  const [saveDrivers] = useMutation(SAVE_DRIVERS, {
    onError: error => {
      showError(error);
      setSaveDisabled(false);
    }
  });

  // #endregion

  // #region Subscriptions

  useConditionalSubscription(DRIVERS_DATA_SAVED, {
    onData: ({ data: { data } }) => {
      resetLens(prev =>
        prev.map(x => ({
          ...x,
          ...data.driversSaved.driverData.drivers.find(y => y.uniqueID === x.uniqueID)
        }))
      );

      addToast(t('General.Saved'), 'success', { toastId: 'driversDataSavedToast' });
      setSaveDisabled(true);
    }
  });

  // #endregion

  // #region Handlers

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleSave = useCallback(() => {
    if (!validate(lens |> get)) {
      return;
    }

    setSaveDisabled(true);

    const drivers = (lens |> get).map(x => {
      return {
        id: x.id,
        uniqueID: x.uniqueID,
        firstName: x.firstName,
        lastName: x.lastName,
        identityCardNo: x.identityCardNo,
        phoneNumber: x.phoneNumber,
        phoneCountryCode: x.phoneCountryCode,
        agreesWithPersonalDataUse: x.agreesWithPersonalDataUse
      };
    });

    saveDrivers({
      variables: { documentId: documentId, drivers: drivers }
    });
    resetValidation();
  }, [documentId, lens, resetValidation, saveDrivers, validate]);

  const handleAdd = useCallback(() => {
    over(lens, append(defaultDriver));
  }, [lens, defaultDriver]);

  const handleDeleteDriver = useCallback(
    index => {
      over(lens, remove(index, 1));
    },
    [lens]
  );

  // #endregion

  // #region Side effects

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validate]);

  useEffect(() => {
    const isDriverItemDirty = isDirty(dirtyInfo);
    const driverItemWithoutGDRP = (lens |> get).some(x => x.agreesWithPersonalDataUse === false);
    setSaveDisabled(!isDriverItemDirty || driverItemWithoutGDRP);

    setDirtyInfoRef('driverData', isDriverItemDirty);
    const { onDirtyInfoChange } = dirtyInfoRef.current;
    onDirtyInfoChange && onDirtyInfoChange();
  }, [dirtyInfo, lens, dirtyInfoRef, setDirtyInfoRef]);

  // #endregion

  return (
    <>
      <Prompt when={isDirty(dirtyInfo)} message={t('LeavingWithoutSaving')} />
      <DriversComponent
        lens={lens}
        defaultCountryCode={defaultCountryCode}
        loading={loading}
        error={error}
        expanded={expanded}
        onToggleExpanded={handleToggleExpanded}
        onDeleteDriver={handleDeleteDriver}
        onSaveDriver={e => {
          e.stopPropagation();
          handleSave();
        }}
        onAddDriver={e => {
          e.stopPropagation();
          handleAdd();
        }}
        saveDisabled={saveDisabled}
        addDisabled={addDisabled}
        validation={validation}
        readOnly={readOnly}
      />
    </>
  );
};

DriversContainer.propTypes = {
  documentId: PropTypes.number,
  readOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};
export default DriversContainer;
